const HASH_KEY = "doc";
const RAW_PREFIX = "raw.";

export function downloadHtml(html: string, filename = "Untitled.html"): void {
  const name = filename.toLowerCase().endsWith(".html") ? filename : `${filename}.html`;
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.rel = "noopener";
  document.body.append(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to legacy path
  }
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.setAttribute("readonly", "");
  ta.style.position = "fixed";
  ta.style.opacity = "0";
  document.body.append(ta);
  ta.select();
  let ok = false;
  try {
    ok = document.execCommand("copy");
  } catch {
    ok = false;
  }
  ta.remove();
  return ok;
}

function bytesToBase64Url(bytes: Uint8Array): string {
  let bin = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(bin).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/, "");
}

function base64UrlToBytes(text: string): Uint8Array {
  const b64 = text.replaceAll("-", "+").replaceAll("_", "/");
  const pad = b64.length % 4 ? "=".repeat(4 - (b64.length % 4)) : "";
  const bin = atob(b64 + pad);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

async function pipe(bytes: Uint8Array, stream: GenericTransformStream): Promise<Uint8Array> {
  const readable = new Blob([bytes as BlobPart]).stream().pipeThrough(stream);
  return new Uint8Array(await new Response(readable).arrayBuffer());
}

function hasCompression(): boolean {
  return typeof CompressionStream !== "undefined" && typeof DecompressionStream !== "undefined";
}

export async function encodeShare(html: string): Promise<string> {
  const bytes = new TextEncoder().encode(html);
  if (hasCompression()) {
    const packed = await pipe(bytes, new CompressionStream("deflate-raw"));
    return bytesToBase64Url(packed);
  }
  return RAW_PREFIX + bytesToBase64Url(bytes);
}

export async function decodeShare(payload: string): Promise<string> {
  if (payload.startsWith(RAW_PREFIX)) {
    return new TextDecoder().decode(base64UrlToBytes(payload.slice(RAW_PREFIX.length)));
  }
  if (!hasCompression()) {
    throw new Error("This browser cannot decode compressed share links");
  }
  const bytes = await pipe(base64UrlToBytes(payload), new DecompressionStream("deflate-raw"));
  return new TextDecoder().decode(bytes);
}

export async function buildShareUrl(html: string, base = location.href): Promise<string> {
  const url = new URL(base);
  url.hash = `${HASH_KEY}=${await encodeShare(html)}`;
  return url.toString();
}

export async function readShareHash(hash = location.hash): Promise<string | null> {
  const match = hash.match(new RegExp(`^#${HASH_KEY}=(.+)$`));
  if (!match) return null;
  try {
    return await decodeShare(match[1]);
  } catch {
    return null;
  }
}

export function formatBytes(count: number): string {
  if (count < 1024) return `${count} B`;
  if (count < 1024 * 1024) return `${(count / 1024).toFixed(1)} KB`;
  return `${(count / (1024 * 1024)).toFixed(2)} MB`;
}
